import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useDeleteConversation } from '@/lib/hooks/useConversations';
import { useUIStore } from '@/lib/store/uiStore';
import { generateConversationTitle } from '@/lib/utils/formatters';
import type { ConversationSummary } from '@/types/conversation';
import { cn } from '@/lib/utils';

interface ConversationItemProps {
  conversation: ConversationSummary;
}

function ConversationItem({ conversation }: ConversationItemProps) {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const deleteMutation = useDeleteConversation();
  const { isMobile, setSidebarOpen } = useUIStore();
  const [isHovered, setIsHovered] = useState(false);

  const isActive = conversationId === conversation.id;
  const title = conversation.title || generateConversationTitle(conversation);

  const handleClick = () => {
    navigate(`/chat/${conversation.id}`);

    // Close sidebar on mobile after selecting conversation
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await deleteMutation.mutateAsync(conversation.id);

      // If we deleted the active conversation, navigate to home
      if (isActive) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group flex items-center gap-3 rounded-md px-3 py-2 transition-all cursor-pointer',
        'hover:bg-gray-100',
        isActive && 'bg-primary-50 text-primary-900 border border-primary-200 hover:bg-primary-100 shadow-sm'
      )}
    >
      {/* Title */}
      <span className={cn(
        'flex-1 truncate text-sm font-medium',
        isActive ? 'text-primary-900' : 'text-gray-900'
      )}>
        {title}
      </span>

      {/* Delete button - shown on hover or mobile */}
      {(isHovered || isMobile) && (
        <Button
          variant="danger"
          size="icon"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="h-8 w-8 transition-all"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default ConversationItem;
